import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'
import { Loader } from '@/components'
import { EmptyListData } from '@/components/ui'
import { useHrInternalHoursHolidaysBalance, useModal } from '@/hooks'
import { HrInternalHoursTable } from '@/features/hrInternalHours/HrInternalHoursTable'
import { HrInternalHoursExportForm } from '@/features/hrInternalHours/HrInternalHoursExportForm'

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]

export const HrInternalHoursContainer = (): JSX.Element => {
  const { t } = useTranslation('hrInternalHours')
  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const { open } = useModal()

  const { data, isLoading } = useHrInternalHoursHolidaysBalance({ year })

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setYear(Number(event.target.value))
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" fontWeight="bold">
          {t('title')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => open(<HrInternalHoursExportForm />)}
          >
            {t('export.button')}
          </Button>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('filters.year')}</InputLabel>
            <Select<number>
              value={year}
              label={t('filters.year')}
              onChange={handleYearChange}
            >
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {isLoading && (
        <Box my={10}>
          <Loader />
        </Box>
      )}

      {!isLoading && data && data.length > 0 && (
        <HrInternalHoursTable rows={data} />
      )}

      {!isLoading && (!data || data.length === 0) && (
        <Box my={10}>
          <EmptyListData textKey="hrInternalHours:table.noData" />
        </Box>
      )}
    </Stack>
  )
}
